import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { map, take } from 'rxjs/operators';
import { appsList } from 'src/app/STORE/selectors/applifecycle.selector';
import { ApiConstants } from 'src/app/base/api-constants';
import { GlobalConstants } from 'src/app/base/global.constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { AppGlobalService } from 'src/app/services/app.global.service';

import { TranslateService } from '@ngx-translate/core';
import { isArray } from 'lodash-es';
import {
  deleteApps,
  deleteUsersFromApp,
  getAllAppsFromSuite,
  getAppById,
  getApps,
  getAppsBasedOnSearch,
  getAppsForOrg,
  getAppsForOrgWithSorting,
  getDBlist,
  getErrorMessage,
  getFullApps,
  getGeneratedAppUrl,
  getGenerationStatus,
  getIndepApps,
  getLastPublish,
  getMemApps,
  getMemDBlist,
  getMemTechnologyStack,
  getOrgs,
  getSidBasedApps,
  getUsersFromApp,
  newAppCreated,
  saveAppSuite,
  saveUserToApp,
  saveUserToAppStas,
  sentApps,
  sentDeleteApps,
  sentUpdateApps,
  setAllApps,
  setAllAppsFromSuite,
  setAppById,
  setAppId,
  setApps,
  setAppsDrop,
  setDBlist,
  setGenStatus,
  setGeneratedAppUrl,
  setIndepApps,
  setLastPublish,
  setMemApps,
  setMemDBlist,
  setMemTechnologyStack,
  setSidBasedApps,
  setUsersFromApp,
  triggerPrototypingAppGeneration,
  updateApps,
  setCurrentAppFullData
} from '../actions/applifecycle.actions';
import { UtilService } from 'src/app/services/util.service';

@Injectable()
export class ApplifecycleEffects {
  private actions$ = inject(Actions);
  private baseService = inject(HttpBaseService);
  private store = inject(Store);
  private appGlobalService = inject(AppGlobalService);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);
  private utilService = inject(UtilService);

  time: any;
  oldRecords: any[] = [];
  filteredList: any[];
  failedStatus = this.utilService.failedStatus;
  successStatus = this.utilService.successStatus;
  public static genUrlTimeout: any;
  public static genStatusTimeout: any;

  removeDuplicates(updateRecords) {
    const ids = updateRecords.map((o) => o.sid);
    this.filteredList = updateRecords.filter(({ sid }, index) => !ids.includes(sid, index + 1));
    return this.filteredList;
  }

  getAppsList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getApps),
        map((action: any) => {
          return this.baseService[action.getAppsApiConstants?.method](
            action.getAppsApiConstants,
            action.filter
          ).subscribe((appsData: any) => {
            this.store.select(appsList).subscribe((oldData: any) => {
              if (!isArray(oldData)) {
                this.oldRecords = [];
              } else {
                this.oldRecords = oldData;
              }
            });
            const updateRecords = [...this.oldRecords, ...appsData.results];
            this.removeDuplicates(updateRecords);
            this.store.dispatch(setApps({ appList: this.filteredList }));
            this.store.dispatch(setAllApps({ allAppList: this.filteredList, totalRecords: appsData.total }));
          });
        })
      ),
    {
      dispatch: false
    }
  );

  getAppsForOrg$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getAppsForOrg),
        map((action: any) => {
          return this.baseService[action.getAppsApiConstants?.method](
            action.getAppsApiConstants,
            action.filter
          ).subscribe((appsData: any) => {
            // Clear old records when org changes (fresh page load/redirect)
            if (action.orgChange) {
              this.oldRecords = [];
            } else {
              this.store.select(appsList).pipe(take(1)).subscribe((oldData: any) => {
                if (!isArray(oldData)) {
                  this.oldRecords = [];
                } else {
                  this.oldRecords = oldData;
                }
              });
            }
            const updateRecords = [...this.oldRecords, ...appsData.results];
            this.removeDuplicates(updateRecords);
            this.store.dispatch(setApps({ appList: this.filteredList }));
            this.store.dispatch(setAppsDrop({ appDropList: this.filteredList }));
            this.store.dispatch(setAllApps({ allAppList: this.filteredList, totalRecords: appsData.total }));
            this.store.dispatch(setAppId({ appId: this.appGlobalService.get('appId') }));
          });
        })
      ),
    {
      dispatch: false
    }
  );

  getAppsForOrgWithSorting$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getAppsForOrgWithSorting),
        map((action: any) => {
          return this.baseService[action.getAppsApiConstants?.method](
            action.getAppsApiConstants,
            action.filter
          ).subscribe((appsData: any) => {
            this.removeDuplicates(appsData.results);
            this.store.dispatch(setApps({ appList: this.filteredList }));
            this.store.dispatch(setAppsDrop({ appDropList: this.filteredList }));
            this.store.dispatch(setAllApps({ allAppList: this.filteredList, totalRecords: appsData.total }));
            this.store.dispatch(setAppId({ appId: this.appGlobalService.get('appId') }));
          });
        })
      ),
    {
      dispatch: false
    }
  );

  getOrgList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getOrgs),
        map((action: any) => {
          return this.baseService[action.getOrgs.method](
            action.getOrgConstants,
            GlobalConstants.dataTableHeaders
          ).subscribe((orgsData: any) => {
            this.store.dispatch(setApps({ appList: orgsData }));
          });
        })
      ),
    {
      dispatch: false
    }
  );

  getSidBasedApps$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getSidBasedApps),
        map((action: any) => {
          const apiConstants = { ...action.apiConstants };
          apiConstants.url = action.apiConstants.url + action.sid;

          return this.baseService[apiConstants?.method](apiConstants).subscribe((sidAppData: any) => {
            this.store.dispatch(
              setSidBasedApps({
                sidAppData: { ...sidAppData }
              })
            );
          });
        })
      ),
    {
      dispatch: false
    }
  );

  // Get User Data Search
  srcText: null | string = null;
  getAppsBasedOnSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getAppsBasedOnSearch),
        map((action: any) => {
          const searchQuery = {
            start: action.start,
            length: action.length,
            search: action.searchFields
          };
          return this.baseService[action.apiConstants?.method](
            action.apiConstants,
            JSON.stringify(searchQuery)
          ).subscribe((appList: any) => {
            this.srcText = '';
            this.removeDuplicates(appList.results);
            this.store.dispatch(setApps({ appList: this.filteredList }));
            this.store.dispatch(setAllApps({ allAppList: this.filteredList, totalRecords: appList.total }));
            this.store.dispatch(setAppsDrop({ appDropList: this.filteredList }));
            this.srcText = '';
            this.store.select(appsList).subscribe((oldData: any) => {
              if (!isArray(oldData)) {
                this.oldRecords = [];
              } else {
                this.oldRecords = oldData;
              }
            });
            const updateRecords = [...this.oldRecords, ...appList.results];
            const ids = updateRecords.map((o) => o.sid);
            const filtered = updateRecords.filter(({ sid }, index) => !ids.includes(sid, index + 1));
            this.store.dispatch(setAllApps({ allAppList: filtered, totalRecords: appList.total }));
            this.store.dispatch(
              setApps({
                appList: appList.results
              })
            );
          });
        })
      ),
    {
      dispatch: false
    }
  );

  getAppById$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getAppById),
        map((action: any) => {
          const apiConstants = { ...action.getAppsApiConstants };
          apiConstants.url = action.getAppsApiConstants.url + action.appId;
          return this.baseService[action.getAppsApiConstants.method](
            apiConstants,
            GlobalConstants.dataTableHeaders
          ).subscribe((appsData: any) => {
            this.store.dispatch(setAppById({ appData: appsData }));
          });
        })
      ),
    { dispatch: false }
  );
  saveAppData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sentApps),
        map((action: any) => {
          return this.baseService[action.getAppsApiConstants?.method](
            action.getAppsApiConstants,
            action.saveAppsData
          ).subscribe(
            (appsData: any) => {
              this.afterAppCreationSucc(action, appsData);
            },
            (err) => {
              this.afterAppCreationErr(err);
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  updateAppData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sentUpdateApps),
        map((action: any) => {
          return this.baseService[action.getAppsApiConstants?.method](
            action.getAppsApiConstants,
            action.updateApps
          ).subscribe((updateAppsData: any) => {
            this.store.dispatch(updateApps({ updateAppsData }));
          });
        })
      ),
    {
      dispatch: false
    }
  );

  triggerAppGeneration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(triggerPrototypingAppGeneration),
        map((action: any) => {
          return this.baseService[action.genAppApi?.method](action.genAppApi, action.genParams).subscribe(
            (appGenerated: any) => {
              this.time = 0;
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  deleteApp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(sentDeleteApps),
        map((action: any) => {
          const apiConstants = { ...action.apiConstants };
          const toDeleteInGit = action.toDeleteInGit ?? true;
          apiConstants.url = `${action.getAppsApiConstants.url}${action.appId}?toDeleteInGit=${toDeleteInGit}`;
          return this.baseService[action.getAppsApiConstants?.method](apiConstants).subscribe(
            (menuData: any) => {
              this.store.select(appsList).subscribe((oldData: any) => {
                if (!isArray(oldData)) {
                  this.oldRecords = [];
                } else {
                  this.oldRecords = oldData;
                }
              });

              const updateRecords = this.oldRecords.filter((x) => x.sid !== action.appId);
              this.store.dispatch(setApps({ appList: updateRecords }));
              this.store.dispatch(setAppsDrop({ appDropList: updateRecords }));
              this.store.dispatch(setAllApps({ allAppList: updateRecords, totalRecords: updateRecords.length }));
              this.showMessage('success', 'Application Deleted Successfully !',10000);
              return this.store.dispatch(
                deleteApps({
                  appId: action.appId
                })
              );
            },
            (error) => {
              this.showMessage(
                'error',
                `An error occurred while attempting to delete the application ${action.appName}. Please try again later. If the issue persists, kindly contact our support team for assistance`,10000
              );
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );
  getGeneratedAppUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getGeneratedAppUrl),
        map((action: any) => {
          const apiConstants = { ...ApiConstants.getAppById };
          let sid = action.sid;
          apiConstants.url = apiConstants.url + action.sid;
          return this.baseService[apiConstants?.method](apiConstants).subscribe((genUrl: any) => {
            this.store.dispatch(setGeneratedAppUrl({ genUrl: genUrl }));
            if (genUrl && !genUrl.prototypingAppUrl) {
              if (action.sid === this.appGlobalService.get('appId')) {
                ApplifecycleEffects.genUrlTimeout = setTimeout(() => {
                  this.store.dispatch(getGeneratedAppUrl({ apiConstants, sid }));
                }, 60.0 * 1000);
              } else {
                sid = this.appGlobalService.get('appId');
                clearTimeout(ApplifecycleEffects.genUrlTimeout);
              }
            }
          });
        })
      ),
    {
      dispatch: false
    }
  );

  saveUserToApp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveUserToApp),
        map((action: any) => {
          this.store.dispatch(saveUserToAppStas({ userSaveStatus: null }));
          return this.baseService[action.saveApiConstants?.method](action.saveApiConstants, action.params).subscribe(
            (_data: any) => {
              this.store.dispatch(saveUserToAppStas({ userSaveStatus: 'success' }));
            },
            (err: any) => {
              this.store.dispatch(saveUserToAppStas({ userSaveStatus: err }));
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  getUsersFromApp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getUsersFromApp),
        map((action: any) => {
          return this.baseService[action.apiConstants.method](action.apiConstants).subscribe((data: any) => {
            this.store.dispatch(setUsersFromApp({ usersBasedOnApp: data }));
          });
        })
      ),
    { dispatch: false }
  );

  deleteUsersFromApp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteUsersFromApp),
        map((action: any) => {
          this.store.dispatch(saveUserToAppStas({ userSaveStatus: null }));
          return this.baseService[action.deleteApiConstants.method](action.deleteApiConstants, action.params).subscribe(
            (_data: any) => {
              this.store.dispatch(saveUserToAppStas({ userSaveStatus: 'success' }));
            },
            (err: any) => {
              this.store.dispatch(saveUserToAppStas({ userSaveStatus: err }));
            }
          );
        })
      ),
    { dispatch: false }
  );

  getLastPublish$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getLastPublish),
        map((action: any) => {
          return this.baseService[action.lastPublishApi?.method](action.lastPublishApi).subscribe((res: any) => {
            this.store.dispatch(
              setLastPublish({
                publishLog: res
              })
            );
          });
        })
      ),
    {
      dispatch: false
    }
  );

  afterAppCreationSucc(action, appsData) {
    const applstSub = this.store.select(appsList).subscribe((oldData: any) => {
      if (!isArray(oldData)) {
        this.oldRecords = [];
      } else {
        this.oldRecords = oldData;
      }
      if (applstSub) {
        applstSub.unsubscribe();
      }
    });
    const updateRecords = [...this.oldRecords];
    if (!action.saveAppsData?.sid) {
      updateRecords.push(appsData);
    } else {
      const indexOfupdateApp = updateRecords.findIndex((x) => x.sid === appsData.sid);
      updateRecords[indexOfupdateApp] = appsData;
    }

    const ids = updateRecords.map((o) => o.sid);
    const filtered = updateRecords.filter(({ sid }, index) => !ids.includes(sid, index + 1));

    this.store.dispatch(setApps({ appList: filtered }));
    this.store.dispatch(setAppsDrop({ appDropList: filtered }));
    this.store.dispatch(setAllApps({ allAppList: filtered, totalRecords: appsData.total }));

    this.store.dispatch(updateApps({ updateAppsData: appsData }));
    this.store.dispatch(
      newAppCreated({
        createdApp: appsData
      })
    );
  }

  afterAppCreationErr(err) {
    console.log(err);
    this.store.dispatch(
      getErrorMessage({
        errorMessage: err
      })
    );

    if (err.error.MESSAGE) {
      if(err.error.MESSAGE == 'SUITE_HAS_SOFT_DELETE' || err.error.MESSAGE == 'SUITE_HAS_NOT_SOFT_DELETE' || err.error.MESSAGE == 'MEMBER_HAS_ELASTIC_SEARCH'){
      this.showMessage('error', this.translate.instant(err.error.MESSAGE),20000);
      }
      else{
      this.showMessage('error', this.translate.instant(err.error.MESSAGE),10000);
      }
    }
  }

  saveAppSuite$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveAppSuite),
        map((action: any) => {
          return this.baseService[action.saveApiConstants?.method](action.saveApiConstants, action.appData).subscribe(
            (appsData: any) => {
              this.afterAppCreationSucc(action, appsData);
            },
            (err) => {
              this.afterAppCreationErr(err);
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  getIndepApps$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getIndepApps),
        map((action: any) => {
          return this.baseService[action.apiConstants?.method](action.apiConstants, action.filter).subscribe(
            (res: any) => {
              this.store.dispatch(
                setIndepApps({
                  indepApps: res
                })
              );
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  getDBlist$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getDBlist),
        map((action: any) => {
          return this.baseService[action.apiConstants?.method](action.apiConstants, action.params).subscribe(
            (res: any) => {
              this.store.dispatch(
                setDBlist({
                  dbList: res
                })
              );
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  getMemDBlist$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getMemDBlist),
        map((action: any) => {
          return this.baseService[action.apiConstants?.method](action.apiConstants, action.params).subscribe(
            (res: any) => {
              this.store.dispatch(
                setMemDBlist({
                  memdbList: res
                })
              );
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  getMemApps$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getMemApps),
        map((action: any) => {
          return this.baseService[action.apiConstants?.method](action.apiConstants, action.params).subscribe(
            (res: any) => {
              this.store.dispatch(
                setMemApps({
                  memAppList: res
                })
              );
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  getMemTechnologyStack$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getMemTechnologyStack),
        map((action: any) => {
          const apiConstants = { ...action.apiConstants };
          apiConstants.url = action.apiConstants.url + action.appId;
          return this.baseService[action.apiConstants.method](apiConstants, GlobalConstants.dataTableHeaders).subscribe(
            (techStackData: any) => {
              if (techStackData) {
                this.store.dispatch(setMemTechnologyStack({ memtechnologyStackData: techStackData }));
              }
            }
          );
        })
      ),
    { dispatch: false }
  );

  getFullApps$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getFullApps),
        map((action: any) => {
          return this.baseService[action.apiConstants?.method](action.apiConstants).subscribe((fullApps: any) => {
            this.store.dispatch(setApps({ appList: fullApps }));
            this.store.dispatch(setAllApps({ allAppList: fullApps, totalRecords: fullApps.length }));
            this.store.dispatch(setAppsDrop({ appDropList: fullApps }));
          });
        })
      ),
    {
      dispatch: false
    }
  );

  showMessage(status: string, message: string,lifespan?:number,detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: lifespan,
      severity: status,
      summary: message,
      detail: detail
    });
  }

  getAllAppsFromSuite$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getAllAppsFromSuite),
        map((action: any) => {
          return this.baseService[action.apiConstants?.method](action.apiConstants, action.params).subscribe(
            (res: any) => {
              this.store.dispatch(
                setAllAppsFromSuite({
                  allAppFromSuiteList: res
                })
              );
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  updateCurrentAppFullData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSidBasedApps, setAppById, updateApps),
      map((action: any) => {
        const response = action.sidAppData || action.appData || action.updateAppsData;
        return setCurrentAppFullData({ currentAppFullData: response });
      })
    )
  );
}

